import { useState, useEffect } from 'react';
import { useSurveyLink } from '@/hooks/queries';
import { useAuthStore, useConfigStore } from '@/store';

/**
 * As all builds use the prod environment, we need to use short cookie name from runtime env var
 * for the dev sites. The shortcookiename contains a `p` for production when coming back from the
 * config api.
 */
const getShortCookieName = (
    shortCookieEnvName: string | undefined,
    shortCookieConfigName: string,
) => {
    if (shortCookieEnvName) {
        return shortCookieEnvName;
    }
    return shortCookieConfigName;
};

export const useSurveyLinkIfShortCookieExists = () => {
    const [showPopup, setShowPopup] = useState(false);
    const [hasShownPopup, setHasShownPopup] = useState(false);
    const [shortCookieDidExist, setShortCookieDidExist] = useState(false);
    const [activeStartLink, setActiveStartLink] = useState<string>();
    const [activePhraseId, setActivePhraseId] = useState<number>();
    const { redirectJwt, setRedirectJwt } = useAuthStore((state) => ({
        redirectJwt: state.redirectJwt,
        setRedirectJwt: state.setRedirectJwt,
    }));

    const hasRedirectedJwtChecked = redirectJwt?.status === 'redirected';
    const shortCookieConfigName = useConfigStore((state) => state.short_cookie_name);
    const shortCookieName = getShortCookieName(
        process.env.NEXT_PUBLIC_SHORT_COOKIE_NAME,
        shortCookieConfigName,
    );
    const shortCookieExists =
        typeof window !== 'undefined' && shortCookieName
            ? document.cookie
                  .split(';')
                  .some((item) => item.trim().startsWith(`${shortCookieName}=`))
            : false;

    const { data: surveyLink } = useSurveyLink(
        hasRedirectedJwtChecked && (shortCookieExists || shortCookieDidExist),
    );

    const activatePopup = (phraseId: number, startLink?: string) => {
        setActivePhraseId(phraseId);
        if (startLink) {
            setActiveStartLink(startLink);
        }
        setShowPopup(true);
        setShortCookieDidExist(false);
    };

    useEffect(() => {
        if (shortCookieExists) {
            setShortCookieDidExist(true);
        }
    }, [shortCookieExists]);

    useEffect(() => {
        if (hasRedirectedJwtChecked) {
            setTimeout(() => {
                setRedirectJwt({ jwt: undefined, status: undefined });
            }, 1000); //short timeout required to allow the popup to show
        }
    }, [hasRedirectedJwtChecked, redirectJwt.jwt, setRedirectJwt]);

    useEffect(() => {
        if (!surveyLink || hasShownPopup) {
            return;
        }
        const { hasUserSurveySpecificProgram, startLink, phraseId } = surveyLink;

        if (hasUserSurveySpecificProgram && phraseId) {
            activatePopup(phraseId, startLink);
        } else {
            setShowPopup(false);
        }
    }, [surveyLink, hasShownPopup]);

    const closePopup = () => {
        setShowPopup(false);
        setHasShownPopup(true);
    };

    return { showPopup, startLink: activeStartLink, phraseId: activePhraseId, closePopup };
};
